import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Eye, ArrowLeft, Plus, GripVertical, Trash2, Copy, ChevronUp, ChevronDown, Undo2, Monitor, Smartphone } from 'lucide-react';
import { SectionRenderer } from '@/components/builder/SectionRenderer';
import { SectionEditor } from '@/components/builder/SectionEditor';
import { SECTION_TEMPLATES } from '@/components/builder/sectionTemplates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAGES = [
  { slug: 'home', label: 'Home Page' },
  { slug: 'about', label: 'About Page' },
];

export default function PageBuilder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(searchParams.get('page') || 'home');
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [history, setHistory] = useState([]);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const canvasRef = useRef(null);

  const loadPage = useCallback(async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/pages/${slug}`);
      const data = await res.json();
      setSections(data.sections || []);
      setHistory([]);
      setEditingSection(null);
    } catch { toast.error('Failed to load page'); }
  }, []);

  useEffect(() => { loadPage(currentPage); }, [currentPage, loadPage]);

  const pushHistory = () => setHistory(prev => [...prev.slice(-19), JSON.stringify(sections)]);

  const handleUndo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, -1));
    setSections(JSON.parse(prev));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('cts_token');
      await fetch(`${API_URL}/api/pages/${currentPage}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: PAGES.find(p => p.slug === currentPage)?.label || currentPage, sections }),
      });
      toast.success('Page saved!');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const updateSection = (id, newProps) => {
    pushHistory();
    setSections(prev => prev.map(s => s.id === id ? { ...s, props: { ...s.props, ...newProps } } : s));
  };

  const deleteSection = (id) => {
    pushHistory();
    setSections(prev => prev.filter(s => s.id !== id));
    if (editingSection === id) setEditingSection(null);
  };

  const duplicateSection = (id) => {
    pushHistory();
    const idx = sections.findIndex(s => s.id === id);
    const copy = { ...JSON.parse(JSON.stringify(sections[idx])), id: crypto.randomUUID() };
    setSections(prev => [...prev.slice(0, idx + 1), copy, ...prev.slice(idx + 1)]);
  };

  const moveSection = (id, dir) => {
    pushHistory();
    const idx = sections.findIndex(s => s.id === id);
    if ((dir === -1 && idx === 0) || (dir === 1 && idx === sections.length - 1)) return;
    const newSections = [...sections];
    [newSections[idx], newSections[idx + dir]] = [newSections[idx + dir], newSections[idx]];
    setSections(newSections);
  };

  const addSection = (type) => {
    pushHistory();
    const template = SECTION_TEMPLATES[type];
    if (!template) return;
    const newSection = { id: crypto.randomUUID(), type, props: JSON.parse(JSON.stringify(template.defaultProps)) };
    setSections(prev => [...prev, newSection]);
    setAddMenuOpen(false);
    setTimeout(() => {
      if (canvasRef.current) canvasRef.current.scrollTo({ top: canvasRef.current.scrollHeight, behavior: 'smooth' });
    }, 100);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    pushHistory();
    const newSections = [...sections];
    const [moved] = newSections.splice(dragIdx, 1);
    newSections.splice(idx, 0, moved);
    setSections(newSections);
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const switchPage = (slug) => {
    setCurrentPage(slug);
    setSearchParams({ page: slug });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-60px)]" data-testid="page-builder">
      {/* Top toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <Select value={currentPage} onValueChange={switchPage}>
            <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="page-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGES.map(p => <SelectItem key={p.slug} value={p.slug}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-gray-400">{sections.length} sections</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleUndo} disabled={history.length === 0} className="h-8 text-xs" title="Undo">
            <Undo2 className="w-3.5 h-3.5" />
          </Button>
          <Button variant={previewMode ? 'default' : 'outline'} size="sm" onClick={() => { setPreviewMode(!previewMode); setEditingSection(null); }} className="h-8 text-xs" data-testid="preview-toggle">
            <Eye className="w-3.5 h-3.5 mr-1" /> {previewMode ? 'Editing' : 'Preview'}
          </Button>
          <Button size="sm" className="h-8 text-xs bg-[#E31937] hover:bg-[#C4122C] text-white" onClick={handleSave} disabled={saving} data-testid="save-page-btn">
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" asChild>
            <a href={`/${currentPage === 'home' ? '' : currentPage}`} target="_blank" rel="noreferrer">
              <Monitor className="w-3.5 h-3.5 mr-1" /> View Live
            </a>
          </Button>
        </div>
      </div>

      {/* Main area: Canvas + Side Editor */}
      <div className="flex flex-1 overflow-hidden">
        {/* Canvas */}
        <div ref={canvasRef} className={`flex-1 overflow-y-auto bg-gray-100 ${previewMode ? '' : 'p-4'}`}>
          {previewMode ? (
            <div className="bg-white">
              {sections.map(section => (
                <SectionRenderer key={section.id} section={section} isPreview />
              ))}
            </div>
          ) : (
            <div className="max-w-[1200px] mx-auto space-y-3">
              {sections.length === 0 && (
                <div className="text-center py-20 text-gray-400">
                  <p className="text-lg mb-2">No sections yet</p>
                  <p className="text-sm mb-4">Add sections using the + button below</p>
                </div>
              )}
              {sections.map((section, idx) => (
                <div
                  key={section.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`group relative bg-white rounded-lg border transition-all ${editingSection === section.id ? 'border-[#E31937] ring-2 ring-[#E31937]/20' : 'border-gray-200 hover:border-gray-300'} ${dragIdx === idx ? 'opacity-50' : ''}`}
                  data-testid={`section-${section.type}-${idx}`}
                >
                  {/* Section toolbar */}
                  <div className="absolute -top-3 left-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                    <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-md shadow-sm px-1.5 py-0.5">
                      <GripVertical className="w-3 h-3 text-gray-400 cursor-grab" />
                      <span className="text-[10px] font-medium text-gray-500 uppercase">{SECTION_TEMPLATES[section.type]?.label || section.type}</span>
                    </div>
                    <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-md shadow-sm px-0.5 py-0.5">
                      <button onClick={() => moveSection(section.id, -1)} className="p-0.5 hover:bg-gray-100 rounded" title="Move up"><ChevronUp className="w-3 h-3 text-gray-500" /></button>
                      <button onClick={() => moveSection(section.id, 1)} className="p-0.5 hover:bg-gray-100 rounded" title="Move down"><ChevronDown className="w-3 h-3 text-gray-500" /></button>
                      <button onClick={() => duplicateSection(section.id)} className="p-0.5 hover:bg-gray-100 rounded" title="Duplicate"><Copy className="w-3 h-3 text-gray-500" /></button>
                      <button onClick={() => deleteSection(section.id)} className="p-0.5 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-3 h-3 text-red-400" /></button>
                    </div>
                  </div>
                  {/* Section content - click to select, double-click to edit inline */}
                  <div
                    className="cursor-pointer overflow-hidden rounded-lg"
                    onClick={() => setEditingSection(section.id)}
                  >
                    <SectionRenderer
                      section={section}
                      isEditing={editingSection === section.id}
                      onUpdate={(newProps) => updateSection(section.id, newProps)}
                    />
                  </div>
                </div>
              ))}

              {/* Add section button */}
              <div className="relative flex justify-center py-4">
                <Button variant="outline" size="sm" className="h-9 text-sm border-dashed border-gray-300 text-gray-500 hover:text-[#E31937] hover:border-[#E31937]" onClick={() => setAddMenuOpen(!addMenuOpen)} data-testid="add-section-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Section
                </Button>
                {addMenuOpen && (
                  <div className="absolute top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-30 w-[400px] grid grid-cols-2 gap-2" data-testid="section-menu">
                    {Object.entries(SECTION_TEMPLATES).map(([type, tpl]) => (
                      <button
                        key={type}
                        onClick={() => addSection(type)}
                        className="flex items-center gap-2 p-2.5 rounded-md border border-gray-100 hover:border-[#E31937] hover:bg-red-50/30 transition-all text-left"
                        data-testid={`add-${type}`}
                      >
                        <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center text-gray-500 flex-shrink-0">
                          <tpl.icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-800">{tpl.label}</p>
                          <p className="text-[10px] text-gray-400">{tpl.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Side editor panel */}
        {editingSection && !previewMode && (
          <div className="w-[320px] border-l border-gray-200 bg-white overflow-y-auto flex-shrink-0">
            <SectionEditor
              section={sections.find(s => s.id === editingSection)}
              onUpdate={(newProps) => updateSection(editingSection, newProps)}
              onClose={() => setEditingSection(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
